import { z } from 'zod'

/**
 * Types and schemas for maintenance-related Firebase Functions
 */

// Delete Inapplicable Tasks schemas
export const DeleteInapplicableTasksRequestSchema = z.object({
  companyId: z.string().min(1, 'Company ID is required'),
  siteId: z.string().min(1, 'Site ID is required'),
  unitId: z.string().min(1, 'Unit ID is required'),
  programId: z.string().min(1, 'Program ID is required'),
  itemId: z.string().min(1, 'Item ID is required'),
  dryRun: z.boolean(),
  // Reason is recorded in the program audit log when committing a deletion.
  // Optional on dry-run; required when dryRun is false (enforced server-side).
  reason: z
    .string()
    .trim()
    .min(5, 'Reason must be at least 5 characters')
    .max(500)
    .optional(),
})

export type DeleteInapplicableTasksRequest = z.infer<
  typeof DeleteInapplicableTasksRequestSchema
>

export interface DeleteInapplicableTasksTaskPreview {
  id: string
  task: string
  codeRef: string | null
  status: 'pending' | 'completed' | 'overdue'
  dueDate: string
}

export interface DeleteInapplicableTasksDryRunResponse {
  success: true
  dryRun: true
  count: number
  tasks: DeleteInapplicableTasksTaskPreview[]
}

export interface DeleteInapplicableTasksDeleteResponse {
  success: true
  dryRun: false
  deletedCount: number
  message: string
}

export type DeleteInapplicableTasksResponse =
  | DeleteInapplicableTasksDryRunResponse
  | DeleteInapplicableTasksDeleteResponse

// Customize Annual Task Month schemas (MCP-480)
export const CustomizeAnnualTaskMonthRequestSchema = z.object({
  companyId: z.string().min(1),
  siteId: z.string().min(1),
  taskCodeRef: z.string().nullable(),
  taskTitle: z.string().min(1),
  unitIds: z.array(z.string().min(1)).min(1).max(200),
  // ISO YYYY-MM-DD, always the first of a valid month
  newDueDate: z
    .string()
    .regex(
      /^\d{4}-(0[1-9]|1[0-2])-01$/,
      'newDueDate must be the first of a valid month (YYYY-MM-01)',
    ),
  reason: z
    .string()
    .trim()
    .min(5, 'Reason must be at least 5 characters')
    .max(500),
})
export type CustomizeAnnualTaskMonthRequest = z.infer<
  typeof CustomizeAnnualTaskMonthRequestSchema
>

export const CustomizeAnnualTaskMonthFailureReason = z.enum([
  'unit_not_found',
  'program_not_found',
  'item_not_found',
  'task_title_mismatch',
  'not_maintenance_task',
  'not_annual_interval',
  'already_completed',
  'out_of_mcp_window',
])
export type CustomizeAnnualTaskMonthFailureReason = z.infer<
  typeof CustomizeAnnualTaskMonthFailureReason
>

export const CustomizeAnnualTaskMonthResponseSchema = z.object({
  successes: z.array(z.object({ unitId: z.string() })),
  failures: z.array(
    z.object({
      unitId: z.string(),
      reason: CustomizeAnnualTaskMonthFailureReason,
      message: z.string(),
    }),
  ),
})
export type CustomizeAnnualTaskMonthResponse = z.infer<
  typeof CustomizeAnnualTaskMonthResponseSchema
>

// Customize Task Interval schemas (MCP-478)
// Allowed intervals are divisors of 12 so the new schedule fits cleanly
// inside the 12-month MCP window.
export const TASK_INTERVAL_VALUES = [1, 2, 3, 4, 6, 12] as const
export type TaskInterval = (typeof TASK_INTERVAL_VALUES)[number]
export const TaskIntervalSchema = z
  .number()
  .int()
  .refine(
    (n): n is TaskInterval =>
      (TASK_INTERVAL_VALUES as readonly number[]).includes(n),
    { message: 'newInterval must be one of 1, 2, 3, 4, 6, or 12' },
  )

export const CustomizeTaskIntervalRequestSchema = z.object({
  companyId: z.string().min(1),
  siteId: z.string().min(1),
  taskCodeRef: z.string().nullable(),
  taskTitle: z.string().min(1),
  unitIds: z.array(z.string().min(1)).min(1).max(200),
  newInterval: TaskIntervalSchema,
  reason: z
    .string()
    .trim()
    .min(5, 'Reason must be at least 5 characters')
    .max(500),
})
export type CustomizeTaskIntervalRequest = z.infer<
  typeof CustomizeTaskIntervalRequestSchema
>

export const CustomizeTaskIntervalFailureReason = z.enum([
  'unit_not_found',
  'program_not_found',
  'item_not_found',
  'task_title_mismatch',
  'not_maintenance_task',
  'interval_unchanged',
  'malformed_item',
  'resource_exhausted',
])
export type CustomizeTaskIntervalFailureReason = z.infer<
  typeof CustomizeTaskIntervalFailureReason
>

export const CustomizeTaskIntervalResponseSchema = z.object({
  successes: z.array(
    z.object({
      unitId: z.string(),
      previousInterval: z.number(),
      newInterval: z.number(),
      occurrencesCreated: z.number(),
      // Pending and overdue occurrences are both deleted and replaced; they
      // are surfaced separately so the audit trail and admin UI can show what
      // was destroyed.
      pendingDeleted: z.number(),
      overdueDeleted: z.number(),
    }),
  ),
  failures: z.array(
    z.object({
      unitId: z.string(),
      reason: CustomizeTaskIntervalFailureReason,
      message: z.string(),
    }),
  ),
})
export type CustomizeTaskIntervalResponse = z.infer<
  typeof CustomizeTaskIntervalResponseSchema
>
